/**
 * Per-endpoint read policies, declared by features next to their api modules.
 *
 * Everything is `network-only` until a feature opts an endpoint in, so adding
 * this layer changes nothing for anyone until they ask it to.
 */

import { withoutFragment } from './url';

import type { OutboxRow } from './store';

export type ReadStrategy = 'network-only' | 'cache-first' | 'network-first';

export interface ReadPolicy {
  strategy: ReadStrategy;
  /** cache-first only: how old a cached copy may be before it is revalidated. */
  ttlMs: number;
  /** The entity a GET reads, so queued writes for it make the cache authoritative. */
  entity?: (url: string) => string;
  /** For list endpoints: fold queued rows (all of them; filter for yours) into a fresh list before it is stored. */
  mergePending?: (data: unknown, allQueued: OutboxRow[]) => unknown;
}

export const NETWORK_ONLY: ReadPolicy = { strategy: 'network-only', ttlMs: 0 };

type Matcher = string | RegExp;

const registry: { matcher: Matcher; policy: ReadPolicy }[] = [];

/** Path of a request url, without its query string or its fragment. */
function pathOf(url: string): string {
  const target = withoutFragment(url);
  const q = target.indexOf('?');
  return q === -1 ? target : target.slice(0, q);
}

/** Declare how reads of an endpoint behave. A string matches the exact path. */
export function registerReadPolicy(matcher: Matcher, policy: ReadPolicy): void {
  registry.push({ matcher, policy });
}

/** The policy for a request url; the most recent matching registration wins. */
export function readPolicyFor(url: string): ReadPolicy {
  const path = pathOf(url);
  for (let i = registry.length - 1; i >= 0; i -= 1) {
    const { matcher, policy } = registry[i]!;
    if (typeof matcher === 'string' ? matcher === path : matcher.test(path)) return policy;
  }
  return NETWORK_ONLY;
}

/** Tests register policies per case; production registers once at import. */
export function resetReadPoliciesForTests(): void {
  registry.length = 0;
}
